import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { getMeeting, getText, mediaUrl } from "../../services/explorerService";
import {
  getSharedMeetingReport,
  getSharedMeetingText,
  sharedMeetingMediaUrl,
} from "../../services/shareMeetingService";
import SageIcon from "../icons/SageIcon";
import { hasUsableSummary } from "./meetingHistoryUtils";
import ShareMeetingReportModal from "./ShareMeetingReportModal";
import "../../styles/meetingHistory.css";
import "./meetingHistoryLanding.css";

const SPEAKER_COLORS = [
  { bg: "#ede9fe", fg: "#6d28d9", bar: "#8b5cf6", soft: "#f5f3ff" },
  { bg: "#d1fae5", fg: "#059669", bar: "#10b981", soft: "#ecfdf5" },
  { bg: "#e0f2fe", fg: "#0284c7", bar: "#0ea5e9", soft: "#f0f9ff" },
  { bg: "#ffedd5", fg: "#c2410c", bar: "#f97316", soft: "#fff7ed" },
  { bg: "#fce7f3", fg: "#be185d", bar: "#ec4899", soft: "#fdf2f8" },
];

function fmtClock(sec) {
  if (sec == null || !Number.isFinite(Number(sec)) || Number(sec) < 0) {
    return "--:--";
  }
  const s = Math.floor(Number(sec));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = s % 60;
  if (h > 0) {
    return `${h}:${String(m).padStart(2, "0")}:${String(ss).padStart(2, "0")}`;
  }
  return `${m}:${String(ss).padStart(2, "0")}`;
}

function fmtDurationShort(sec) {
  if (sec == null || !Number.isFinite(Number(sec))) return "—";
  const m = Math.max(1, Math.round(Number(sec) / 60));
  return `${m}m`;
}

function initials(name) {
  const parts = String(name || "?")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function parseIso(value) {
  if (!value) return NaN;
  const ms = Date.parse(value);
  return Number.isNaN(ms) ? NaN : ms;
}

function parseTranscript(jsonl, recordingStartedAt) {
  const anchorMs = parseIso(recordingStartedAt);
  const rows = [];
  for (const line of String(jsonl || "").split("\n")) {
    const t = line.trim();
    if (!t) continue;
    try {
      const row = JSON.parse(t);
      if (row.partial) continue;
      const text = (row.text || "").trim();
      if (!text) continue;
      const ts = row.logged_at || row.ts || null;
      const tsMs = parseIso(ts);
      rows.push({
        name: row.speaker_name || row.speaker || "Speaker",
        role: row.role || "",
        text,
        ts,
        tsMs: Number.isNaN(tsMs) ? null : tsMs,
        offset: null,
      });
    } catch {
      /* skip */
    }
  }

  let t0 = Number.isNaN(anchorMs) ? null : anchorMs;
  if (t0 == null) {
    for (const r of rows) {
      if (r.tsMs != null) {
        t0 = r.tsMs;
        break;
      }
    }
  }
  if (t0 != null) {
    for (const r of rows) {
      if (r.tsMs != null) {
        r.offset = Math.max(0, (r.tsMs - t0) / 1000);
      }
    }
  }
  return rows;
}

function Avatar({ name, color, size = 32 }) {
  return (
    <span
      className="avatar"
      style={{
        width: size,
        height: size,
        background: color.bg,
        color: color.fg,
        fontSize: size > 36 ? 14 : 11,
      }}
      aria-hidden
    >
      {initials(name)}
    </span>
  );
}

function ChevronLeftIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M10 3.5L5.5 8 10 12.5"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function LockIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
    >
      <rect
        x="3.5"
        y="7"
        width="9"
        height="7"
        rx="1.5"
        stroke="currentColor"
        strokeWidth="1.4"
      />
      <path
        d="M5.5 7V5.5a2.5 2.5 0 0 1 5 0V7"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
      />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
    >
      <rect
        x="2.5"
        y="2.5"
        width="11"
        height="11"
        rx="2"
        stroke="currentColor"
        strokeWidth="1.4"
      />
      <path
        d="M5 8.2l2 2 4-4"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ChatBubbleIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M3 3.5h10a1 1 0 0 1 1 1v5a1 1 0 0 1-1 1H7l-3 2.2V10.5H3a1 1 0 0 1-1-1v-5a1 1 0 0 1 1-1z"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function SageUnavailable({ feature }) {
  return (
    <div className="sage-empty">
      <div className="sage-empty__icon" aria-hidden>
        <SageIcon size={28} />
      </div>
      <h3>{feature} unavailable</h3>
      <p>
        Sage was not in this meeting, so AI insights were not generated. Invite
        Sage next time for summary, transcript, action items, and key moments.
      </p>
    </div>
  );
}

function RecordingPlaceholder({ attendees, colorMap }) {
  const people = (attendees || []).slice(0, 2);
  while (people.length < 2) {
    people.push({ name: people.length === 0 ? "Host" : "Guest" });
  }
  return (
    <div className="player player--placeholder" aria-label="No recording">
      <div className="player__split">
        {people.map((p, i) => {
          const name = p.name || `Guest ${i + 1}`;
          const color =
            colorMap?.get(name) || SPEAKER_COLORS[i % SPEAKER_COLORS.length];
          return (
            <div
              key={`${name}-${i}`}
              className="player__split-pane"
              style={{ background: color.bar }}
            >
              <span>{initials(name)}</span>
            </div>
          );
        })}
      </div>
      <p className="player__placeholder-msg">
        No recording available for this meeting
      </p>
    </div>
  );
}

function VideoPlayer({ src, videoRef, onTime }) {
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [rate, setRate] = useState(1);
  const [fullscreen, setFullscreen] = useState(false);
  const playerRef = useRef(null);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const onMeta = () => setDuration(v.duration || 0);
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    const onUpdate = () => {
      setCurrent(v.currentTime || 0);
      onTime?.(v.currentTime || 0);
    };
    v.addEventListener("loadedmetadata", onMeta);
    v.addEventListener("durationchange", onMeta);
    v.addEventListener("timeupdate", onUpdate);
    v.addEventListener("play", onPlay);
    v.addEventListener("pause", onPause);
    return () => {
      v.removeEventListener("loadedmetadata", onMeta);
      v.removeEventListener("durationchange", onMeta);
      v.removeEventListener("timeupdate", onUpdate);
      v.removeEventListener("play", onPlay);
      v.removeEventListener("pause", onPause);
    };
  }, [videoRef, onTime, src]);

  useEffect(() => {
    const syncFs = () => {
      const el = playerRef.current;
      const stage = el?.querySelector(".player__stage");
      const active = Boolean(
        el &&
        (document.fullscreenElement === el ||
          document.webkitFullscreenElement === el),
      );
      setFullscreen(active);
      el?.classList.toggle("player--fs", active);
      // Inline overrides beat any leftover max-height:52vh / aspect-ratio
      if (stage) {
        if (active) {
          stage.style.flex = "1 1 0%";
          stage.style.height = "0";
          stage.style.maxHeight = "none";
          stage.style.minHeight = "0";
          stage.style.aspectRatio = "unset";
        } else {
          stage.style.flex = "";
          stage.style.height = "";
          stage.style.maxHeight = "";
          stage.style.minHeight = "";
          stage.style.aspectRatio = "";
        }
      }
    };
    document.addEventListener("fullscreenchange", syncFs);
    document.addEventListener("webkitfullscreenchange", syncFs);
    return () => {
      document.removeEventListener("fullscreenchange", syncFs);
      document.removeEventListener("webkitfullscreenchange", syncFs);
    };
  }, []);

  function toggle() {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) v.play().catch(() => {});
    else v.pause();
  }

  function seekBy(delta) {
    const v = videoRef.current;
    if (!v) return;
    v.currentTime = Math.max(
      0,
      Math.min(v.duration || 0, v.currentTime + delta),
    );
  }

  function onScrub(e) {
    const v = videoRef.current;
    if (!v || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = Math.min(
      1,
      Math.max(0, (e.clientX - rect.left) / rect.width),
    );
    v.currentTime = ratio * duration;
  }

  function cycleRate() {
    const next =
      rate >= 2
        ? 0.75
        : rate >= 1.5
          ? 2
          : rate >= 1.25
            ? 1.5
            : rate >= 1
              ? 1.25
              : 1;
    setRate(next);
    if (videoRef.current) videoRef.current.playbackRate = next;
  }

  async function toggleFs() {
    const wrap = playerRef.current;
    if (!wrap) return;
    try {
      if (
        document.fullscreenElement === wrap ||
        document.webkitFullscreenElement === wrap
      ) {
        if (document.exitFullscreen) await document.exitFullscreen();
        else if (document.webkitExitFullscreen)
          await document.webkitExitFullscreen();
        return;
      }
      if (wrap.requestFullscreen) await wrap.requestFullscreen();
      else if (wrap.webkitRequestFullscreen)
        await wrap.webkitRequestFullscreen();
      // Ensure FS layout applies even if the change event is delayed
      wrap.classList.add("player--fs");
      const stage = wrap.querySelector(".player__stage");
      if (stage) {
        stage.style.flex = "1 1 0%";
        stage.style.height = "0";
        stage.style.maxHeight = "none";
        stage.style.minHeight = "0";
        stage.style.aspectRatio = "unset";
      }
      setFullscreen(true);
    } catch (err) {
      console.warn("Fullscreen failed:", err?.message || err);
    }
  }

  const pct = duration > 0 ? (current / duration) * 100 : 0;

  return (
    <div className={`player${fullscreen ? " player--fs" : ""}`} ref={playerRef}>
      <div className="player__stage">
        <video
          ref={videoRef}
          className="player__video"
          src={src}
          preload="metadata"
          playsInline
          onClick={toggle}
        />
      </div>
      <div className="player__controls">
        <button
          type="button"
          className="player__btn"
          onClick={toggle}
          aria-label={playing ? "Pause" : "Play"}
        >
          {playing ? "❚❚" : "▶"}
        </button>
        <button
          type="button"
          className="player__btn"
          onClick={() => seekBy(-10)}
          aria-label="Back 10s"
        >
          ⟲10
        </button>
        <button
          type="button"
          className="player__btn"
          onClick={() => seekBy(10)}
          aria-label="Forward 10s"
        >
          10⟳
        </button>
        <div
          className="player__scrub"
          onClick={onScrub}
          role="slider"
          aria-valuenow={current}
          aria-valuemin={0}
          aria-valuemax={duration || 0}
        >
          <div className="player__scrub-fill" style={{ width: `${pct}%` }} />
        </div>
        <span className="player__time mono">
          {fmtClock(current)} / {fmtClock(duration)}
        </span>
        <button
          type="button"
          className="player__btn player__rate"
          onClick={cycleRate}
          aria-label="Playback speed"
        >
          {rate}x
        </button>
        <button
          type="button"
          className="player__btn"
          onClick={toggleFs}
          aria-label={fullscreen ? "Exit fullscreen" : "Enter fullscreen"}
        >
          ⛶
        </button>
      </div>
    </div>
  );
}

function TranscriptPanel({
  rows,
  colorMap,
  videoRef,
  currentTime,
  synced,
  sagePresent,
  searchQuery = "",
  onSearchQueryChange,
}) {
  const [speaker, setSpeaker] = useState("all");
  const [activeIdx, setActiveIdx] = useState(-1);
  const rowRefs = useRef([]);

  const speakers = useMemo(() => {
    const names = [];
    for (const r of rows) {
      if (!names.includes(r.name)) names.push(r.name);
    }
    return names;
  }, [rows]);

  const filtered = useMemo(() => {
    const needle = searchQuery.trim().toLowerCase();
    return rows
      .map((r, i) => ({ ...r, _i: i }))
      .filter((r) => {
        if (speaker !== "all" && r.name !== speaker) return false;
        if (needle && !r.text.toLowerCase().includes(needle)) return false;
        return true;
      });
  }, [rows, speaker, searchQuery]);

  useEffect(() => {
    if (!synced || !rows.length) {
      setActiveIdx(-1);
      return;
    }
    let idx = -1;
    for (let i = 0; i < rows.length; i += 1) {
      const off = rows[i].offset;
      if (off != null && off <= currentTime + 0.35) idx = i;
    }
    setActiveIdx(idx);
  }, [currentTime, rows, synced]);

  useEffect(() => {
    if (activeIdx < 0) return;
    const el = rowRefs.current[activeIdx];
    if (el) el.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [activeIdx]);

  function seekTo(offset) {
    const v = videoRef.current;
    if (!v || offset == null) return;
    v.currentTime = offset;
    v.play?.().catch(() => {});
  }

  function exportTxt() {
    const text = filtered
      .map((r) => {
        const t = r.offset != null ? fmtClock(r.offset) : "--:--";
        return `[${t}] ${r.name}${r.role ? ` (${r.role})` : ""}: ${r.text}`;
      })
      .join("\n");
    const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "transcript.txt";
    a.click();
    URL.revokeObjectURL(a.href);
  }

  if (!rows.length) {
    if (!sagePresent) {
      return <SageUnavailable feature="Transcript" />;
    }
    return (
      <div className="empty">No transcript captured for this meeting.</div>
    );
  }

  return (
    <div className="tx">
      <div className="tx__toolbar">
        <div className="tx__filters">
          <button
            type="button"
            className={`chip ${speaker === "all" ? "chip--on" : ""}`}
            onClick={() => setSpeaker("all")}
          >
            All speakers
          </button>
          {speakers.map((name) => (
            <button
              key={name}
              type="button"
              className={`chip ${speaker === name ? "chip--on" : ""}`}
              onClick={() => setSpeaker(name)}
            >
              {name}
            </button>
          ))}
        </div>
        {/* <div className="tx__tools">
          <button type="button" className="btn btn-ghost btn-sm" onClick={exportTxt}>
            Export .txt
          </button>
        </div> */}
      </div>
      {!synced && (
        <div className="tx__note">
          Timeline sync unavailable — timestamps shown when possible. Click a
          line once offsets are known.
        </div>
      )}
      <div className="tx__list">
        {filtered.map((r) => {
          const color = colorMap.get(r.name) || SPEAKER_COLORS[0];
          const active = r._i === activeIdx;
          const clickable = synced && r.offset != null;
          return (
            <button
              type="button"
              key={r._i}
              ref={(el) => {
                rowRefs.current[r._i] = el;
              }}
              className={`tx__row ${active ? "tx__row--active" : ""} ${
                clickable ? "tx__row--click" : ""
              }`}
              onClick={() => clickable && seekTo(r.offset)}
            >
              <span className="tx__time mono">
                {r.offset != null ? fmtClock(r.offset) : "—"}
              </span>
              <Avatar name={r.name} color={color} size={34} />
              <div className="tx__body">
                <span className="tx__name" style={{ color: color.fg }}>
                  {r.name}
                </span>
                <p>{r.text}</p>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function SummaryPanel({ summary, sagePresent }) {
  if (!sagePresent) {
    return <SageUnavailable feature="AI Summary" />;
  }
  if (!summary) {
    return (
      <div className="empty">No AI summary available yet for this meeting.</div>
    );
  }
  const overview = summary.overview || summary.summary || "";
  const strengths = summary.strengths || [];
  const concerns = summary.concerns || [];
  const topics = summary.topics_covered || [];
  const next = summary.recommended_next_steps || [];

  return (
    <div className="sum">
      {overview && (
        <section>
          <h3>Overview</h3>
          <p>{overview}</p>
        </section>
      )}
      {topics.length > 0 && (
        <section>
          <h3>Key topics</h3>
          <ul>
            {topics.map((t, i) => (
              <li key={i}>{t}</li>
            ))}
          </ul>
        </section>
      )}
      {strengths.length > 0 && (
        <section>
          <h3>Strengths</h3>
          <ul>
            {strengths.map((t, i) => (
              <li key={i}>{t}</li>
            ))}
          </ul>
        </section>
      )}
      {concerns.length > 0 && (
        <section>
          <h3>Concerns</h3>
          <ul>
            {concerns.map((t, i) => (
              <li key={i}>{t}</li>
            ))}
          </ul>
        </section>
      )}
      {next.length > 0 && (
        <section>
          <h3>Recommended next steps</h3>
          <ul>
            {next.map((t, i) => (
              <li key={i}>{t}</li>
            ))}
          </ul>
        </section>
      )}
      {summary.hiring_signal && (
        <p className="sum__signal">
          Hiring signal: <strong>{summary.hiring_signal}</strong>
        </p>
      )}
    </div>
  );
}

function ActionsPanel({ items, sagePresent }) {
  if (!sagePresent) {
    return <SageUnavailable feature="Action items" />;
  }
  if (!items?.length) {
    return (
      <div className="empty">No action items extracted from this meeting.</div>
    );
  }
  return (
    <ul className="actions">
      {items.map((item, i) => (
        <li key={i}>{item}</li>
      ))}
    </ul>
  );
}

export default function MeetingHistoryDetail({
  roomName,
  companyId,
  recruiterId,
  onBack,
  shareToken = "",
  readOnly = false,
}) {
  const isSharedView = Boolean(shareToken);
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState("summary");
  const [transcriptRaw, setTranscriptRaw] = useState("");
  const [summary, setSummary] = useState(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [txSearch, setTxSearch] = useState("");
  const [shareOpen, setShareOpen] = useState(false);
  const videoRef = useRef(null);

  const notifyShareParent = useCallback((status, message = "") => {
    if (!isSharedView || typeof window === "undefined") return;
    try {
      if (window.parent && window.parent !== window) {
        window.parent.postMessage(
          {
            source: "meetgeek-shared-report",
            status,
            message: message || "",
          },
          "*",
        );
      }
    } catch {
      /* ignore */
    }
  }, [isSharedView]);

  useEffect(() => {
    let alive = true;
    setError(null);
    setDetail(null);
    setTranscriptRaw("");
    setSummary(null);
    setTab("summary");

    const loadOwner = async () => {
      if (!companyId || !recruiterId) {
        throw new Error(
          "Open MeetGeek from GetGeek with your company and recruiter context to view this meeting.",
        );
      }
      const d = await getMeeting(roomName, { companyId, recruiterId });
      if (!alive) return;
      setDetail(d);
      const jobs = [];
      if (d.files?.transcript) {
        jobs.push(
          getText(roomName, "transcript", { companyId, recruiterId })
            .then((t) => alive && setTranscriptRaw(t))
            .catch(() => {}),
        );
      }
      if (d.files?.summary) {
        jobs.push(
          getText(roomName, "summary", { companyId, recruiterId })
            .then((t) => {
              if (!alive) return;
              try {
                setSummary(JSON.parse(t));
              } catch {
                setSummary({ overview: t });
              }
            })
            .catch(() => {}),
        );
      }
      await Promise.all(jobs);
      if (alive && !d.files?.transcript && d.files?.summary) {
        setTab("summary");
      }
    };

    const loadShared = async () => {
      const d = await getSharedMeetingReport(shareToken);
      if (!alive) return;
      setDetail(d);
      const jobs = [];
      if (d.files?.transcript) {
        jobs.push(
          getSharedMeetingText(shareToken, "transcript")
            .then((t) => alive && setTranscriptRaw(t))
            .catch(() => {}),
        );
      }
      if (d.files?.summary) {
        jobs.push(
          getSharedMeetingText(shareToken, "summary")
            .then((t) => {
              if (!alive) return;
              try {
                setSummary(JSON.parse(t));
              } catch {
                setSummary({ overview: t });
              }
            })
            .catch(() => {}),
        );
      }
      await Promise.all(jobs);
      if (alive && !d.files?.transcript && d.files?.summary) {
        setTab("summary");
      }
      if (alive) notifyShareParent("ready");
    };

    (isSharedView ? loadShared() : loadOwner()).catch((e) => {
      if (!alive) return;
      setError(e.message);
      if (isSharedView) {
        notifyShareParent("error", e.message || "Failed to load report");
      }
    });
    return () => {
      alive = false;
    };
  }, [
    roomName,
    companyId,
    recruiterId,
    shareToken,
    isSharedView,
    notifyShareParent,
  ]);

  const rows = useMemo(
    () => parseTranscript(transcriptRaw, detail?.recordingStartedAt),
    [transcriptRaw, detail?.recordingStartedAt],
  );

  const colorMap = useMemo(() => {
    const map = new Map();
    let i = 0;
    for (const r of rows) {
      if (!map.has(r.name)) {
        map.set(r.name, SPEAKER_COLORS[i % SPEAKER_COLORS.length]);
        i += 1;
      }
    }
    for (const p of detail?.speakingTime || []) {
      const name = p.name || "Speaker";
      if (!map.has(name)) {
        map.set(name, SPEAKER_COLORS[i % SPEAKER_COLORS.length]);
        i += 1;
      }
    }
    return map;
  }, [rows, detail?.speakingTime]);

  const synced = useMemo(
    () => rows.length > 0 && rows.some((r) => r.offset != null),
    [rows],
  );

  const attendees = useMemo(() => {
    const fromSpeak = (detail?.speakingTime || []).filter((p) => {
      const name = String(p?.name || "").toLowerCase();
      const role = String(p?.role || "").toLowerCase();
      if (role === "sage" || role === "agent" || role === "bot") return false;
      return !(name === "sage" || name.startsWith("sage "));
    });
    if (fromSpeak.length) return fromSpeak;
    const seen = new Map();
    for (const r of rows) {
      const name = String(r.name || "").toLowerCase();
      if (name === "sage" || name.startsWith("sage ")) continue;
      if (!seen.has(r.name)) seen.set(r.name, { name: r.name, role: r.role });
    }
    if (seen.size) return [...seen.values()];
    if (detail?.hostName) {
      return [{ name: detail.hostName, role: "host" }];
    }
    return [];
  }, [detail?.speakingTime, detail?.hostName, rows]);

  const actionItems = useMemo(() => {
    if (!summary) return [];
    return summary.recommended_next_steps || summary.action_items || [];
  }, [summary]);

  const keyTopics = summary?.topics_covered || [];
  const keyMoments = detail?.keyMoments || [];
  const hasRecording = Boolean(detail?.files?.recording);
  const recordingSrc = isSharedView
    ? sharedMeetingMediaUrl(shareToken, "recording")
    : mediaUrl(roomName, "recording", { companyId, recruiterId });

  const onTime = useCallback((t) => setCurrentTime(t), []);

  const seekTo = useCallback((sec) => {
    const v = videoRef.current;
    if (!v || sec == null || !Number.isFinite(Number(sec))) return;
    v.currentTime = Number(sec);
    v.play?.().catch(() => {});
  }, []);

  const timeRangeLabel = useMemo(() => {
    const start = detail?.recordingStartedAt || detail?.scheduledStartTime;
    const end = detail?.finalizedAt || detail?.scheduledEndTime;
    if (!start) return null;
    const s = new Date(start);
    const e = end ? new Date(end) : null;
    const date = s.toLocaleDateString(undefined, {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
    const t0 = s.toLocaleTimeString(undefined, {
      hour: "numeric",
      minute: "2-digit",
    });
    const t1 = e
      ? e.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })
      : null;
    return { date, range: t1 ? `${t0} – ${t1}` : t0 };
  }, [detail]);

  if (error) {
    if (isSharedView) {
      // Parent share panel shows EmptyState; keep iframe quiet
      return <div className="review review--shared-shell" />;
    }
    return (
      <div className="review">
        <button className="btn btn-ghost back" onClick={onBack}>
          ‹ All meetings
        </button>
        <div className="error">Failed to load: {error}</div>
      </div>
    );
  }

  if (!detail) {
    // Shared iframe: blank shell — parent overlay is the only loader
    if (isSharedView) {
      return <div className="review review--shared-shell" />;
    }
    return <div className="loading">Loading meeting…</div>;
  }

  const speakTotal =
    (detail.speakingTime || []).reduce(
      (acc, p) => acc + Number(p.speaking_seconds || 0),
      0,
    ) || 1;

  const sageOn = detail.sagePresent === true;
  const shareEnabled = hasUsableSummary(detail);

  return (
    <>
    <div className={`review${sageOn ? "" : " review--no-sage"}`}>
      <header className="mg-hist-page__head review__page-head">
        <div className="mg-hist-page__title-block">
          {!isSharedView && !readOnly ? (
            <button
              type="button"
              className="mg-hist-back"
              onClick={onBack}
              aria-label="Back to meetings"
            >
              <ChevronLeftIcon />
            </button>
          ) : null}
          <div>
            <h1>{detail.title || "Untitled meeting"}</h1>
            <div className="review__meta">
              <span className="pill pill--ok">
                {detail.artifactsStatus === "complete" ||
                detail.status === "ended" ||
                hasRecording
                  ? "Recorded"
                  : detail.status || "Meeting"}
              </span>
              {sageOn ? (
                <span className="pill pill--sage">
                  <SageIcon size={14} /> Sage joined
                </span>
              ) : null}
              {timeRangeLabel && (
                <span className="review__meta-text">
                  {timeRangeLabel.date}
                  <span className="dot">·</span>
                  {timeRangeLabel.range}
                </span>
              )}
              <span className="review__meta-text">
                {attendees.length} participant
                {attendees.length === 1 ? "" : "s"}
              </span>
            </div>
          </div>
        </div>
        <div className="review__head-actions">
          {!isSharedView && !readOnly ? (
          <button
            type="button"
            className="review__action-btn"
            disabled={!shareEnabled}
            title={
              shareEnabled
                ? "Share meeting report"
                : "Available when Sage summary is ready"
            }
            onClick={() => setShareOpen(true)}
          >
            <svg
              width="15"
              height="15"
              viewBox="0 0 16 16"
              fill="none"
              aria-hidden="true"
            >
              <circle
                cx="12"
                cy="4"
                r="2"
                stroke="currentColor"
                strokeWidth="1.4"
              />
              <circle
                cx="4"
                cy="8"
                r="2"
                stroke="currentColor"
                strokeWidth="1.4"
              />
              <circle
                cx="12"
                cy="12"
                r="2"
                stroke="currentColor"
                strokeWidth="1.4"
              />
              <path
                d="M5.8 7.1L10.2 4.9M5.8 8.9L10.2 11.1"
                stroke="currentColor"
                strokeWidth="1.4"
              />
            </svg>
            Share
          </button>
          ) : null}
        </div>
      </header>

      <div className="review__grid">
        <div className="review__main">
          {hasRecording ? (
            <VideoPlayer
              src={recordingSrc}
              videoRef={videoRef}
              onTime={onTime}
            />
          ) : (
            <RecordingPlaceholder attendees={attendees} colorMap={colorMap} />
          )}

          <section className="tabs-card">
            <div className="tabs-bar">
              <div className="tabs-bar__tabs" role="tablist">
                <button
                  type="button"
                  role="tab"
                  aria-selected={tab === "summary"}
                  className={`tab ${tab === "summary" ? "tab-active" : ""}`}
                  onClick={() => setTab("summary")}
                >
                  <SageIcon size={14} />
                  <span>AI Summary</span>
                  {!sageOn && (
                    <span className="tab__lock" title="Requires Sage">
                      <LockIcon />
                    </span>
                  )}
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={tab === "transcript"}
                  className={`tab ${tab === "transcript" ? "tab-active" : ""}`}
                  onClick={() => setTab("transcript")}
                >
                  <ChatBubbleIcon />
                  <span>Transcript</span>
                  <span className="tab__badge">{rows.length}</span>
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={tab === "actions"}
                  className={`tab ${tab === "actions" ? "tab-active" : ""}`}
                  onClick={() => setTab("actions")}
                >
                  <CheckIcon />
                  <span>Action Items</span>
                  <span className="tab__badge">
                    {sageOn ? actionItems.length : 0}
                  </span>
                  {!sageOn && (
                    <span className="tab__lock" title="Requires Sage">
                      <LockIcon />
                    </span>
                  )}
                </button>
              </div>
              {tab === "transcript" && (sageOn || rows.length > 0) && (
                <label className="tabs-search">
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 16 16"
                    fill="none"
                    aria-hidden="true"
                  >
                    <circle
                      cx="7"
                      cy="7"
                      r="4.5"
                      stroke="currentColor"
                      strokeWidth="1.5"
                    />
                    <path
                      d="M10.5 10.5L13.5 13.5"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                  </svg>
                  <input
                    type="search"
                    placeholder="Search transcript..."
                    value={txSearch}
                    onChange={(e) => setTxSearch(e.target.value)}
                  />
                </label>
              )}
            </div>

            {!sageOn && (
              <div className="tabs-banner" role="status">
                <LockIcon />
                <span>
                  Sage was not invited — AI summary, transcript, and action
                  items are unavailable for this meeting.
                </span>
              </div>
            )}

            <div className="tabs-body" role="tabpanel">
              {tab === "summary" && (
                <SummaryPanel summary={summary} sagePresent={sageOn} />
              )}
              {tab === "transcript" && (
                <TranscriptPanel
                  rows={rows}
                  colorMap={colorMap}
                  videoRef={videoRef}
                  currentTime={currentTime}
                  synced={synced && hasRecording}
                  sagePresent={sageOn}
                  searchQuery={txSearch}
                  onSearchQueryChange={setTxSearch}
                />
              )}
              {tab === "actions" && (
                <ActionsPanel items={actionItems} sagePresent={sageOn} />
              )}
            </div>
          </section>
        </div>

        <aside className="review__side">
          <section className="side-card">
            <h3>Attendees</h3>
            <ul className="attendees">
              {attendees.map((p) => {
                const name = p.name || "Speaker";
                const color = colorMap.get(name) || SPEAKER_COLORS[0];
                return (
                  <li key={name}>
                    <Avatar name={name} color={color} />
                    <div>
                      <div className="attendees__name">{name}</div>
                      <div className="attendees__role muted">
                        {p.role || "participant"}
                      </div>
                    </div>
                  </li>
                );
              })}
              {!attendees.length && (
                <li className="muted">No attendees listed.</li>
              )}
            </ul>
          </section>

          <section className="side-card">
            <h3>Speaking time</h3>
            {!sageOn ? (
              <div className="side-empty">
                <LockIcon /> Speaking-time breakdown requires Sage
              </div>
            ) : (
              <div className="speak">
                {(detail.speakingTime || [])
                  .filter((p) => {
                    const n = String(p?.name || "").toLowerCase();
                    const r = String(p?.role || "").toLowerCase();
                    return !(
                      r === "sage" ||
                      r === "agent" ||
                      n === "sage" ||
                      n.startsWith("sage ")
                    );
                  })
                  .map((p) => {
                    const name = p.name || "Speaker";
                    const color = colorMap.get(name) || SPEAKER_COLORS[0];
                    const ratio =
                      p.talk_ratio != null
                        ? Number(p.talk_ratio)
                        : Number(p.speaking_seconds || 0) / speakTotal;
                    const pct = Math.round(
                      Math.max(0, Math.min(1, ratio)) * 100,
                    );
                    return (
                      <div key={name} className="speak__row">
                        <div className="speak__label">
                          <span>{name}</span>
                          <span className="mono">{pct}%</span>
                        </div>
                        <div className="speak__track">
                          <div
                            className="speak__fill"
                            style={{ width: `${pct}%`, background: color.bar }}
                          />
                        </div>
                      </div>
                    );
                  })}
                {!(detail.speakingTime || []).length && (
                  <div className="muted empty-sm">No speaking-time data.</div>
                )}
              </div>
            )}
          </section>

          <section className="side-card">
            <h3>Meeting stats</h3>
            <div className="stats-3">
              <div className="stat-mini">
                <strong>{fmtDurationShort(detail.totalMeetingSeconds)}</strong>
                <span>Duration</span>
              </div>
              <div className={`stat-mini${!sageOn ? " is-disabled" : ""}`}>
                <strong>{sageOn ? actionItems.length : "—"}</strong>
                <span>Action items</span>
              </div>
              <div className={`stat-mini${!sageOn ? " is-disabled" : ""}`}>
                <strong>
                  {sageOn ? keyTopics.length || keyMoments.length : "—"}
                </strong>
                <span>Key topics</span>
              </div>
            </div>
            {!sageOn && (
              <p className="side-card__hint">Requires Sage for AI stats</p>
            )}
          </section>

          <section className="side-card">
            <h3>Key moments</h3>
            {!sageOn ? (
              <div className="side-empty">
                <LockIcon /> Key moments require Sage
              </div>
            ) : keyMoments.length === 0 ? (
              <div className="muted empty-sm">No key moments.</div>
            ) : (
              <ul className="moments">
                {keyMoments.map((m, i) => {
                  const sec = Number(m.timestamp_sec);
                  return (
                    <li key={i}>
                      <button
                        type="button"
                        className="moment"
                        onClick={() => Number.isFinite(sec) && seekTo(sec)}
                        disabled={!hasRecording || !Number.isFinite(sec)}
                      >
                        <span className="moment__t mono">
                          {m.timestamp_label || fmtClock(sec)}
                        </span>
                        <span className="moment__label">
                          {m.title || "Moment"}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        </aside>
      </div>
    </div>
    {!isSharedView && !readOnly ? (
      <ShareMeetingReportModal
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        roomName={roomName}
        meetingTitle={detail.title || "Meeting report"}
        companyId={companyId}
        recruiterId={recruiterId}
        canShare={shareEnabled}
      />
    ) : null}
    </>
  );
}
